// RolesPage.tsx
import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageContainer } from '@/components/layout/PageHeader'
import { DataTable } from '@/components/shared/DataTable/DataTable'
import { DataTableToolbar } from '@/components/shared/DataTable/DataTableToolbar'
import { Button, Badge, Card, CardContent } from '@/components/ui'
import { adminApi } from '@/modules/admin/api/admin.api'
import type { AdminRole } from '@/modules/admin/api/admin.api'

export default function RolesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => adminApi.getRoles({ page: 1, limit: 100 }),
  })

  const deleteRole = useMutation({
    mutationFn: (id: string) => adminApi.deleteRole(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-roles'] }),
  })

  // FIX: sécuriser l'extraction — getRoles normalise en { data: [], meta: {} }
  const roles: AdminRole[] = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? (data as any) : []
  const filtered = roles.filter((r) =>
    search ? r.name.toLowerCase().includes(search.toLowerCase()) : true,
  )

  const columns: ColumnDef<AdminRole>[] = [
    {
      accessorKey: 'name',
      header: 'Rôle',
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-[var(--tx)]">{row.original.name}</p>
          {row.original.description && (
            <p className="text-xs text-[var(--tx3)]">{row.original.description}</p>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'isSystem',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant={row.original.isSystem ? 'purple' : 'info'} size="sm">
          {row.original.isSystem ? 'Système' : 'Personnalisé'}
        </Badge>
      ),
    },
    {
      accessorKey: '_count.userRoles',
      header: 'Utilisateurs',
      cell: ({ row }) => (
        <span className="text-[var(--tx2)]">{row.original._count?.userRoles ?? 0}</span>
      ),
    },
    {
      accessorKey: '_count.rolePermissions',
      header: 'Permissions',
      cell: ({ row }) => (
        <span className="text-[var(--tx2)]">{row.original._count?.rolePermissions ?? 0}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const r = row.original
        return (
          <div className="flex items-center gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/roles/${r.id}`)}>
              Voir
            </Button>
            {!r.isSystem && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { if (confirm(`Supprimer le rôle "${r.name}" ?`)) deleteRole.mutate(r.id) }}
                className="text-[var(--red-text)] hover:text-red-300"
              >
                Supprimer
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <PageContainer>
      <PageHeader
        title="Rôles & Permissions"
        description="Gérez les rôles système et personnalisés"
        breadcrumb={[{ label: 'Administration' }, { label: 'Rôles' }]}
        actions={
          <Button onClick={() => setShowCreateModal(true)} size="sm">
            + Nouveau rôle
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b border-[var(--bd)]">
            <DataTableToolbar
              onSearch={setSearch}
              searchPlaceholder="Rechercher un rôle..."
            />
          </div>
          <DataTable
            columns={columns}
            data={filtered}
            isLoading={isLoading}
            emptyTitle="Aucun rôle"
          />
        </CardContent>
      </Card>

      {showCreateModal && (
        <CreateRoleModal onClose={() => { setShowCreateModal(false); queryClient.invalidateQueries({ queryKey: ['admin-roles'] }) }} />
      )}
    </PageContainer>
  )
}

function CreateRoleModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')

  const create = useMutation({
    mutationFn: () => adminApi.createRole({ name: name.toUpperCase().replace(/\s+/g, '_'), description }),
    onSuccess: onClose,
    onError: (err: any) => setError(err?.response?.data?.message ?? 'Erreur'),
  })

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
      <div className="nx-card w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Créer un rôle</h2>
          <button onClick={onClose} className="text-[var(--tx3)] hover:text-[var(--tx)]">✕</button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="bg-[var(--red-bg)] border border-[rgba(239,68,68,0.25)] rounded-lg p-3 text-sm text-[var(--red-text)]">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Nom du rôle *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500 uppercase"
              placeholder="MON_ROLE"
            />
            <p className="text-xs text-[var(--tx3)] mt-1">Sera converti en MAJUSCULES_AVEC_TIRETS</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500 resize-none"
              placeholder="Description optionnelle..."
            />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-[var(--bd)]">
          <Button variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
          <Button className="flex-1" onClick={() => create.mutate()} isLoading={create.isPending} disabled={!name}>
            Créer
          </Button>
        </div>
      </div>
    </div>
  )
}
